import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { CORPORATE_DOCS_FAQ_CATEGORIES } from "@/content/corporate-docs-corpus/faq-defs";

export const Route = createFileRoute("/corporate-docs/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/corporate-docs-corpus");
    return { faqs: m.CORPORATE_DOCS_FAQ_TH };
  },
  component: CorporateDocsFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `เอกสารนิติบุคคลและหนังสือมอบอำนาจธุรกิจข้ามประเทศ — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องหนังสือรับรองบริษัท มติที่ประชุมกรรมการและผู้ถือหุ้น หนังสือมอบอำนาจนิติบุคคลข้ามประเทศ อำนาจลงนามและตราประทับ การรับรองโดยทนายความผู้ทำคำรับรองลายมือชื่อ นิติกรณ์กรมการกงสุลและ Apostille การตั้งบริษัทย่อย/สาขาต่างประเทศ KYC/UBO เครื่องหมายการค้า และเอกสาร M&A",
        path: "/corporate-docs/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/corporate-docs/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ เอกสารนิติบุคคลและหนังสือมอบอำนาจธุรกิจข้ามประเทศ", url: `${site}/corporate-docs/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/corporate-docs/faq")) },
      ],
    };
  },
});

function CorporateDocsFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ เอกสารนิติบุคคลและหนังสือมอบอำนาจธุรกิจข้ามประเทศ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          เอกสารนิติบุคคลและหนังสือมอบอำนาจธุรกิจข้ามประเทศ — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบอ้างอิงประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 3 (หุ้นส่วนบริษัทและตัวแทน) พระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 บัญชีอัตราอากรแสตมป์ท้ายประมวลรัษฎากร แนวปฏิบัติของกรมพัฒนาธุรกิจการค้า (dbd.go.th) กรมการกงสุล (consular.mfa.go.th) ระเบียบสภาทนายความว่าด้วยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร และกรมทรัพย์สินทางปัญญา (ipthailand.go.th) โดย Apostille จะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570 ข้อมูลนี้เป็นข้อมูลทั่วไป โปรดตรวจสอบกับหน่วยงานปลายทางก่อนดำเนินการ
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/corporate-docs/faq" className="text-primary underline">Corporate documents &amp; cross-border POA FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {CORPORATE_DOCS_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/corporate-docs/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามรายการเอกสารนิติบุคคล การร่างหนังสือมอบอำนาจ และการรับรองนิติกรณ์ได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="corporate-docs" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}